<template>
  <div
    v-if="isVisible && status?.message"
    class="performance-status-overlay"
    @click.self="handleDismiss"
  >
    <div class="performance-status-card">
      <div class="performance-status-card__header">
        <span class="performance-status-card__icon">!</span>
        <span class="performance-status-card__title">
          {{ $t("performanceStatus.title") }}
        </span>
      </div>
      <div class="performance-status-card__body" v-html="status.message" />
      <a
        v-if="status.statusLink"
        :href="status.statusLink"
        target="_blank"
        rel="noopener noreferrer"
        class="performance-status-card__link"
      >
        {{ $t("performanceStatus.checkStatus") }}
      </a>
      <button
        type="button"
        class="performance-status-card__close"
        @click="handleDismiss"
      >
        {{ $t("performanceStatus.close") }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from "vue";
import { usePerformanceStatus } from "@/composables/usePerformanceStatus";
import { PERFORMANCE_STATUS_SESSION_KEY } from "@/utils/obs";

export default {
  name: "PerformanceStatusBar",
  props: {
    pollInterval: {
      type: Number,
      default: 5 * 60 * 1000,
    },
  },
  setup(props) {
    const { status } = usePerformanceStatus(props.pollInterval);
    const isDismissed = ref(false);

    const hasShownInSession = () =>
      sessionStorage.getItem(PERFORMANCE_STATUS_SESSION_KEY) === "true";

    const isVisible = computed(
      () =>
        status.value?.degraded &&
        status.value?.message &&
        !isDismissed.value &&
        !hasShownInSession()
    );

    watch(status, () => {
      if (!status.value?.degraded) {
        isDismissed.value = false;
      }
    });

    function handleDismiss() {
      isDismissed.value = true;
      sessionStorage.setItem(PERFORMANCE_STATUS_SESSION_KEY, "true");
    }

    return { status, isVisible, handleDismiss };
  },
};
</script>

<style lang="scss" scoped>
.performance-status-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.35);
}

.performance-status-card {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.performance-status-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
}

.performance-status-card__icon {
  color: #ffa726;
  font-weight: 700;
}

.performance-status-card__title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.performance-status-card__body {
  padding: 16px 20px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.performance-status-card__link {
  display: block;
  text-align: center;
  margin: 0 20px 12px;
  color: #0088f5;
  font-size: 14px;
}

.performance-status-card__close {
  display: block;
  width: calc(100% - 40px);
  margin: 0 20px 20px;
  padding: 10px 24px;
  border: none;
  border-radius: 24px;
  background: #0088f5;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.performance-status-card__close:hover {
  background: #0066cc;
}
</style>
